import type {
  AgeRange,
  DayTicketRate,
  TicketRate,
  ActivityAvailabilityResponse,
  GraphQL,
  AvailableTimeslotsPrices,
  TimeSlot,
} from '@libs/trip-planner-interfaces';

import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';

import { toMoney } from './moneyResponseQL';

export function toActivityAvailabilityResponse(
  ticketResponseQL: GraphQL.searchTicketAvailabilityQuery
): ActivityAvailabilityResponse {
  assertSmartSearchValue(ticketResponseQL.getTicketRates);
  assertSmartSearchList(ticketResponseQL.getTicketRates.ratesByDay);

  const ratesByDay =
    ticketResponseQL.getTicketRates.ratesByDay.map<DayTicketRate>(
      (rateByDay) => {
        assertSmartSearchValue(rateByDay.date);

        assertSmartSearchList(rateByDay.rates);

        const ticketRates = rateByDay.rates.map(toTicketRate);

        return { date: rateByDay.date, rates: ticketRates };
      }
    );

  return {
    hasFixedDates: ticketResponseQL.getTicketRates.hasFixedDates ?? false,
    ratesByDay,
  };
}

type GetTicketRatesQL = NonNullable<
  GraphQL.searchTicketAvailabilityQuery['getTicketRates']
>;
type RatesByDayQL = NonNullable<GetTicketRatesQL['ratesByDay']>;
type RateByDay = NonNullable<RatesByDayQL[number]>;
type TicketRatesQL = NonNullable<RateByDay['rates']>;
type TicketRateQL = NonNullable<TicketRatesQL[number]>;
function toTicketRate(ticketRateQL: TicketRateQL): TicketRate {
  assertSmartSearchList(ticketRateQL.ageRanges);
  assertSmartSearchValue(ticketRateQL.rateId);
  assertSmartSearchValue(ticketRateQL.title);

  const ageRanges = ticketRateQL.ageRanges.map(toAgeRange);

  const requiredAgeRanges =
    ticketRateQL.requiredAgeRanges?.map(toRequiredAgeRange);

  const timeSlots = ticketRateQL.timeSlots?.map(toTimeSlot);

  return {
    ...ticketRateQL,
    ageRanges,
    requiredAgeRanges,
    timeSlots,
    rateId: ticketRateQL.rateId,
    title: ticketRateQL.title,
    minimumAmountTickets: ticketRateQL.minimumAmountTickets ?? undefined,
    maximumAmountTickets: ticketRateQL.maximumAmountTickets ?? undefined,
    description: ticketRateQL.description ?? undefined,
    dateMainEvent: ticketRateQL.dateMainEvent ?? undefined,
    ticketStartDate: ticketRateQL.ticketStartDate ?? undefined,
    ticketEndDate: ticketRateQL.ticketEndDate ?? undefined,
  };
}

type AgeRangesQL = NonNullable<TicketRateQL['ageRanges']>;
type AgeRangeQL = NonNullable<AgeRangesQL[number]>;
function toAgeRange(ageRangeQL: AgeRangeQL): AgeRange {
  assertSmartSearchValue(ageRangeQL.price);
  assertSmartSearchValue(ageRangeQL.ageRangeId);
  assertSmartSearchValue(ageRangeQL.description);
  const price = toMoney(ageRangeQL.price);

  const availableTimeslotsPrices =
    ageRangeQL.availableTimeslotsPrices?.map<AvailableTimeslotsPrices>(
      toAvailableTimeslotsPrice
    );

  return {
    ...ageRangeQL,
    ageRangeId: ageRangeQL.ageRangeId,
    description: ageRangeQL.description,
    maxAge: ageRangeQL.maxAge ?? null,
    price,
    availableTimeslotsPrices,
    minimumTicketsPerAgeRange:
      ageRangeQL.minimumTicketsPerAgeRange ?? undefined,
    quantityAvailable: ageRangeQL.quantityAvailable ?? undefined,
    supplierToken: ageRangeQL.supplierToken ?? undefined,
  };
}

type AvailableTimelotsPricesQL = NonNullable<
  AgeRangeQL['availableTimeslotsPrices']
>;
type AvailableTimeSlotPriceQL = AvailableTimelotsPricesQL[number];
function toAvailableTimeslotsPrice(
  availableTimeSlotPriceQL: AvailableTimeSlotPriceQL
): AvailableTimeslotsPrices {
  assertSmartSearchValue(availableTimeSlotPriceQL);
  assertSmartSearchValue(availableTimeSlotPriceQL.currency);
  assertSmartSearchValue(availableTimeSlotPriceQL.currencySymbol);

  return {
    ...availableTimeSlotPriceQL,
    currency: availableTimeSlotPriceQL.currency,
    currencySymbol: availableTimeSlotPriceQL.currencySymbol,
    availabletimeSlotId:
      availableTimeSlotPriceQL.availabletimeSlotId ?? undefined,
    quantityAvailable: availableTimeSlotPriceQL.quantityAvailable ?? undefined,
  };
}

type RequiredAgeRangesQL = NonNullable<TicketRateQL['requiredAgeRanges']>;
type RequiredAgeRangeQL = RequiredAgeRangesQL[number];
function toRequiredAgeRange(requiredAgeRangeQL: RequiredAgeRangeQL): string {
  assertSmartSearchValue(requiredAgeRangeQL);

  return requiredAgeRangeQL;
}

type TimeSlotsQL = NonNullable<TicketRateQL['timeSlots']>;
type TimeSlotQL = TimeSlotsQL[number];
function toTimeSlot(timeSlotQL: TimeSlotQL): TimeSlot {
  assertSmartSearchValue(timeSlotQL);

  assertSmartSearchValue(timeSlotQL.startTime);
  const startTime = timeSlotQL.startTime;
  assertSmartSearchValue(timeSlotQL.timeSlotId);

  return {
    ...timeSlotQL,
    startTime,
    timeSlotId: timeSlotQL.timeSlotId,
  };
}
